/**
 * useTemplates — the React binding for the templates feature.
 *
 * Strict layering: pages call THIS hook; the hook calls {@link TemplateStore};
 * the store calls the api facade. Pages never import the store or the api
 * directly.
 *
 * Exposes the grouped list (with loading/error), a refresh, and thin async
 * mutation/read operations that delegate to the store.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { TemplateStore } from './templateStore';
import { groupTemplateSummaries } from './templateModel';
import { seedDefaultTemplates } from './defaultTemplates';
import type { Template, TemplateGroup, TemplateSummary } from './types';

export interface UseTemplatesResult {
  /** All non-deleted version summaries (flat). */
  summaries: TemplateSummary[];
  /** Summaries grouped by name, latest-version-first, sorted by recency. */
  groups: TemplateGroup[];
  loading: boolean;
  error: string | null;
  /** Re-fetches the list from the store. */
  refresh: () => Promise<void>;
  /** Loads a full template document by id (hydrates the S3 body). */
  getById: (id: string) => Promise<Template | undefined>;
  /** Creates a new template (version resolved automatically). */
  create: (
    draft: Pick<Template, 'name' | 'description' | 'fields' | 'outputs'>,
  ) => Promise<Template>;
  /** Saves the editor content as a brand-new version of the name. */
  saveNewVersion: (
    draft: Pick<Template, 'name' | 'description' | 'fields' | 'outputs'>,
  ) => Promise<Template>;
  /** Updates an existing version document in place. */
  update: (template: Template) => Promise<Template>;
  /** Soft-deletes a version document. */
  remove: (id: string) => Promise<void>;
  /**
   * Idempotently seeds the built-in default templates (B+C+W estimation + Task)
   * if they are absent, then refreshes. Safe to call on page load; only writes
   * when a default name has no existing version. Returns the names created.
   */
  ensureDefaults: () => Promise<string[]>;
}

export function useTemplates(): UseTemplatesResult {
  const api = useApi();
  const store = useMemo(() => new TemplateStore(api), [api]);

  const [summaries, setSummaries] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await store.listSummaries();
      setSummaries(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const groups = useMemo(
    () => groupTemplateSummaries(summaries),
    [summaries],
  );

  const getById = useCallback(
    (id: string) => store.get(id),
    [store],
  );

  const create = useCallback(
    async (
      draft: Pick<Template, 'name' | 'description' | 'fields' | 'outputs'>,
    ) => {
      const saved = await store.create(draft);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const saveNewVersion = useCallback(
    async (
      draft: Pick<Template, 'name' | 'description' | 'fields' | 'outputs'>,
    ) => {
      const saved = await store.saveNewVersion(draft);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const update = useCallback(
    async (template: Template) => {
      const saved = await store.update(template);
      await refresh();
      return saved;
    },
    [store, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await store.softDelete(id);
      await refresh();
    },
    [store, refresh],
  );

  const ensureDefaults = useCallback(async () => {
    const created = await seedDefaultTemplates(store);
    if (created.length > 0) await refresh();
    return created;
  }, [store, refresh]);

  return {
    summaries,
    groups,
    loading,
    error,
    refresh,
    getById,
    create,
    saveNewVersion,
    update,
    remove,
    ensureDefaults,
  };
}
